import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useMapEvents, Polygon, Marker, Tooltip, Polyline } from 'react-leaflet';
import { latLngToCell, cellToBoundary } from 'h3-js';
import { COLORS, Beacon } from '../types';
import styled from 'styled-components';
import { ethers } from 'ethers';
import { getAllBeacons, getBeacon, setBeacon } from '../api/api';
import BeaconMarker from './BeaconMarker';
import Measure from './Measure';
import { LatLngExpression } from 'leaflet';
import { useWebRTC } from '../hooks/useWebRTC';
// import { Tooltip } from 'recharts';
import L from 'leaflet';

const ModalWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000000;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalContent = styled.div`
  background-color: #1f1f1f88;
  padding: 20px;
  border-radius: 8px;
  color: #ffffff;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  margin-bottom: 100px;
`;

const Title = styled.h2`
  margin-top: 0;
  color: ${COLORS.green};
`;

const Button = styled.button`
  padding: 8px 16px;
  background-color: ${COLORS.pink};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 8px;
  margin-right: 16px;
`;

const FloatingButton = styled.button`
  position: absolute;
  bottom: 20px;
  left: 20px;
  padding: 10px 16px;
  background-color: ${COLORS.green};
  color: black;
  border: none;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  
  &:hover {
    background-color: ${COLORS.blue};
    color: white;
  }
`;

const ConnectedAddressWrapper = styled.div`
  color: white;
  position: absolute;
  top: 0;
  right: 10px;
  padding: 5px;
  z-index: 10000000;
`;

const ETHEREUM_MAINNET_CHAIN_ID = '0x1';

export const TestnetMapContent = () => {



  const [provider, setProvider] = useState<ethers.BrowserProvider | null>(null);
  const [signer, setSigner] = useState<ethers.Signer | undefined>();
  const [address, setAddress] = useState<string | undefined>();
  const [currentChainId, setCurrentChainId] = useState<string | undefined>();
  const [polygon, setPolygon] = useState<number[][]>();
  const [outerPolygon, setOuterPolygon] = useState<number[][]>();
  const [beacons, setBeacons] = useState<Beacon[]>([]);
  const [userBeacon, setUserBeacon] = useState<Beacon | null>(null);
  const [selectedBeaconId, setSelectedBeaconId] = useState<string | null>(null);
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [registeringCell, setRegisteringCell] = useState<string | undefined>();
  const [showRegistrationButton, setShowRegistrationButton] = useState(false);
  const [showNetworkModal, setShowNetworkModal] = useState(false);

  const { connect, disconnect, status, rtt, lowestRtt, peerId, connectionDetails } = useWebRTC(userBeacon?.beaconId);
  const [connectionPath, setConnectionPath] = useState<{ local: [number, number]; remote: [number, number] } | null>(null);

  const accountsChangedRef = useRef<((accounts: string[]) => void) | null>(null);
  const chainChangedRef = useRef<((chainId: string) => void) | null>(null);
  const disconnectRef = useRef<(() => void) | null>(null);

  const isMainnet = currentChainId === ETHEREUM_MAINNET_CHAIN_ID;

  // GeoIP lookup for connection details
  useEffect(() => {
    if (!connectionDetails || !isMeasuring) {
      setConnectionPath(null);
      return;
    }

    const isPrivateIP = (ip: string) => {
      return (
        ip.startsWith('10.') ||
        ip.startsWith('192.168.') ||
        ip.startsWith('172.') ||
        /^127\./.test(ip)
      );
    };

    const resolveIPs = async () => {
      try {
        const { local, remote } = connectionDetails;
        if (isPrivateIP(local.ip) || isPrivateIP(remote.ip)) {
          console.warn('Skipping GeoIP for private IPs:', { localIp: local.ip, remoteIp: remote.ip });
          return;
        }

        const localGeo = await fetch(`https://ipwho.is/${local.ip}`).then(res => res.json());
        const remoteGeo = await fetch(`https://ipwho.is/${remote.ip}`).then(res => res.json());

        if (localGeo.success && remoteGeo.success) {
          setConnectionPath({
            local: [localGeo.latitude, localGeo.longitude],
            remote: [remoteGeo.latitude, remoteGeo.longitude],
          });
        } else {
          console.warn('GeoIP lookup failed:', { localGeo, remoteGeo });
        }
      } catch (error) {
        console.error('GeoIP lookup error:', error);
        setConnectionPath(null);
      }
    };
    resolveIPs();
  }, [connectionDetails, isMeasuring]);

  useEffect(() => {
    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getBrowserProvider = useCallback(async () => {
    if (typeof window === 'undefined' || !window.ethereum) {
      throw new Error('No Ethereum provider available');
    }
    return new ethers.BrowserProvider(window.ethereum);
  }, []);

  const switchToMainnet = useCallback(async () => {
    if (typeof window === 'undefined' || !window.ethereum) return;
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: ETHEREUM_MAINNET_CHAIN_ID }],
      });
      setShowNetworkModal(false);
    } catch (error: any) {
      console.error('Failed to switch network:', error);
      if (error.code === 4902) {
        alert('Ethereum Mainnet needs to be added to your wallet.');
      }
    }
  }, []);

  const checkUserBeacon = useCallback(async (userAddress: string) => {
    try {
      const beacon = await getBeacon(userAddress);
      console.log('Checking for beacon with address:', userAddress, 'Result:', beacon);
      setUserBeacon(beacon || null);
    } catch (error) {
      console.log('User does not have a beacon yet');
      setUserBeacon(null);
    }
  }, []);

  const updateSigner = useCallback(async (currentProvider: ethers.BrowserProvider) => {
    try {
      const signer = await currentProvider.getSigner();
      setSigner(signer);
      const address = await signer.getAddress();
      setAddress(address.toLowerCase());
      await checkUserBeacon(address.toLowerCase());
    } catch (error) {
      console.error('Error updating signer:', error);
      setSigner(undefined);
      setAddress(undefined);
      setUserBeacon(null);
    }
  }, [checkUserBeacon]);

  const getChainId = useCallback(async (currentProvider: ethers.Provider) => {
    try {
      const network = await currentProvider.getNetwork();
      const chainId = `0x${network.chainId.toString(16)}`;
      setCurrentChainId(chainId);
      return chainId;
    } catch (error) {
      console.error('Error getting chain ID:', error);
    }
  }, []);

  const setupListeners = useCallback((currentProvider: ethers.BrowserProvider) => {
    if (typeof window === 'undefined' || !window.ethereum) return;

    const handleAccountsChanged = (accounts: string[]) => {
      if (accounts.length === 0) {
        setAddress(undefined);
        setSigner(undefined);
        setUserBeacon(null);
      } else {
        updateSigner(currentProvider);
      }
    };

    const handleChainChanged = (chainId: string) => {
      setCurrentChainId(chainId);
      if (chainId === ETHEREUM_MAINNET_CHAIN_ID) {
        setShowNetworkModal(false);
      }
      updateSigner(currentProvider);
    };

    const handleDisconnect = () => {
      setAddress(undefined);
      setSigner(undefined);
      setProvider(null);
      setCurrentChainId(undefined);
      setUserBeacon(null);
    };

    accountsChangedRef.current = handleAccountsChanged;
    chainChangedRef.current = handleChainChanged;
    disconnectRef.current = handleDisconnect;

    window.ethereum.on('accountsChanged', handleAccountsChanged);
    window.ethereum.on('chainChanged', handleChainChanged);
    window.ethereum.on('disconnect', handleDisconnect);
  }, [updateSigner]);

  const connectWallet = useCallback(async () => {
    try {
      const browserProvider = await getBrowserProvider();
      setProvider(browserProvider);
      await window.ethereum?.request({ method: 'eth_requestAccounts' });
      await updateSigner(browserProvider);
      const chainId = await getChainId(browserProvider);
      setupListeners(browserProvider);
      if (chainId !== ETHEREUM_MAINNET_CHAIN_ID) {
        setShowNetworkModal(true);
      }
    } catch (error) {
      console.error('Error connecting wallet:', error);
      alert('Failed to connect wallet. Please try again.');
    }
  }, [getBrowserProvider, updateSigner, getChainId, setupListeners]);

  const fetchBeacons = useCallback(async () => {
    try {
      const beaconData = await getAllBeacons();
      setBeacons(beaconData);

      // If we have an address, check if any of the beacons belong to the user
      if (address) {
        const userBeaconFromList = beaconData.find(b => b.beaconId.toLowerCase() === address.toLowerCase());
        if (userBeaconFromList) {
          console.log('Found user beacon in beacons list:', userBeaconFromList);
          setUserBeacon(userBeaconFromList);
        }
      }
    } catch (error) {
      console.error('Error fetching beacons:', error);
    }
  }, [address]);

  const registerBeacon = useCallback(async () => {
    if (!provider || !address || !signer || !registeringCell) return;
    if (!isMainnet) {
      setShowNetworkModal(true);
      return;
    }

    const domain = {
      name: 'Proximum Syene Testnet',
      version: '1',
      chainId: 1,
    };

    const types = {
      BeaconAssertion: [
        { name: 'cell', type: 'string' },
        { name: 'hotWallet', type: 'address' },
        { name: 'nonce', type: 'uint256' },
      ],
    };

    const value = {
      cell: registeringCell,
      hotWallet: address,
      nonce: 0,
    };

    try {
      // no-op. TODO: validate signature
      await signer.signTypedData(domain, types, value);

      const beacon = {
        beaconId: address,
        h3Tile: registeringCell,
        metadata: {
          protocol: "webrtc",
          sdp: "dummy-sdp",
        }
      };
      await setBeacon(beacon);
      setUserBeacon(beacon);
      await fetchBeacons();
      // alert(`Beacon registered at H3 location ${registeringCell}.`);
      setRegisteringCell(undefined);
      // Exit the registration flow
      setShowRegistrationButton(false);
    } catch (error) {
      console.error('Error registering beacon:', error);
      alert(`Failed to register beacon.:${error}`);
    }
  }, [provider, signer, address, registeringCell, isMainnet, fetchBeacons]);

  const handleSelectBeacon = useCallback((beaconId: string) => {
    if (!userBeacon) {
      alert('Register your beacon first.');
      return;
    }
    if (beaconId === userBeacon.beaconId) {
      alert('Cannot measure distance to your own beacon.');
      return;
    }

    setSelectedBeaconId(beaconId);
    setIsMeasuring(true);
    connect(beaconId);
  }, [userBeacon, connect]);

  useEffect(() => {
    // First fetch all beacons
    fetchBeacons();

    // Then check if wallet is already connected
    if (typeof window !== 'undefined' && window.ethereum) {
      const checkConnection = async () => {
        try {
          const accounts = await window.ethereum?.request({ method: 'eth_accounts' });
          if (accounts.length > 0) {
            await connectWallet();
          }
        } catch (error) {
          console.error('Error checking connection:', error);
        }
      };
      checkConnection();
    }

    // Cleanup event listeners on unmount
    return () => {
      if (typeof window !== 'undefined' && window.ethereum) {
        if (accountsChangedRef.current) {
          window.ethereum.removeListener('accountsChanged', accountsChangedRef.current);
        }
        if (chainChangedRef.current) {
          window.ethereum.removeListener('chainChanged', chainChangedRef.current);
        }
        if (disconnectRef.current) {
          window.ethereum.removeListener('disconnect', disconnectRef.current);
        }
      }
    };
  }, [connectWallet, fetchBeacons]);

  const MapEvents = () => {
    const map = useMapEvents({
      mousemove: (e) => {
        if (signer && showRegistrationButton) {
          const h3Index = latLngToCell(e.latlng.lat, e.latlng.lng, 6);
          const coordinates = cellToBoundary(h3Index).map(coord => [coord[0], coord[1]]);
          setPolygon(coordinates);

          const outerH3Index = latLngToCell(e.latlng.lat, e.latlng.lng, 2);
          const outerCoordinates = cellToBoundary(outerH3Index).map(coord => [coord[0], coord[1]]);
          setOuterPolygon(outerCoordinates);
        }
      },
      click: (e) => {
        // Only process map clicks if we're in registration mode and no modals are showing
        if (showNetworkModal || !signer || !showRegistrationButton) return;

        const h3Index = latLngToCell(e.latlng.lat, e.latlng.lng, 6);
        // Zoom to a more appropriate level (less zoomed in)
        map.setZoomAround(e.latlng, 9);
        setRegisteringCell(h3Index);
      },
    });
    return null;
  };

  return (
    <>
      {showRegistrationButton && outerPolygon && <Polygon positions={outerPolygon as LatLngExpression[]} pathOptions={{ color: COLORS.blue }} />}
      {showRegistrationButton && polygon && <Polygon positions={polygon as LatLngExpression[]} pathOptions={{ color: COLORS.pink }} />}
      <MapEvents />
      {beacons.map(beacon => (
        <BeaconMarker
          key={beacon.beaconId}
          beacon={beacon}
          isOwnBeacon={userBeacon?.beaconId === beacon.beaconId}
          isSelected={selectedBeaconId === beacon.beaconId}
          userHasBeacon={!!userBeacon}
          isMeasuring={isMeasuring}
          onSelect={handleSelectBeacon}
        />
      ))}
      {isMeasuring && userBeacon && selectedBeaconId && (
        <Measure
          myBeacon={userBeacon}
          targetBeacon={beacons.find(b => b.beaconId === selectedBeaconId)!}
          status={status}
          rtt={rtt}
          lowestRtt={lowestRtt}
          peerId={peerId}
          onClose={() => {
            setIsMeasuring(false);
          }}
        />
      )}
      {connectionPath && (
        <>
          <Marker position={connectionPath.local} icon={L.divIcon({ className: 'local-marker', html: `<div style="background-color: ${COLORS.green}; width: 10px; height: 10px; border-radius: 50%;"></div>` })}>
            <Tooltip permanent>Local Peer ({connectionDetails?.local.type})</Tooltip>
          </Marker>
          <Marker position={connectionPath.remote} icon={L.divIcon({ className: 'remote-marker', html: `<div style="background-color: ${COLORS.blue}; width: 10px; height: 10px; border-radius: 50%;"></div>` })}>
            <Tooltip permanent>Remote Peer ({connectionDetails?.remote.type})</Tooltip>
          </Marker>
          <Polyline positions={[connectionPath.local, connectionPath.remote]} pathOptions={{ color: COLORS.orange, weight: 3, dashArray: '5, 5' }}>
            <Tooltip permanent>WebRTC Path (RTT: {connectionDetails?.rtt?.toFixed(2) || 'unknown'} ms)</Tooltip>
          </Polyline>
        </>
      )}
      {signer && (
        <ConnectedAddressWrapper>
          <h3>
            {address?.slice(0, 5)}...{address?.slice(-3)}
            {!isMainnet && ' (Wrong Network)'}
            {userBeacon && ' (Beacon Registered)'}
          </h3>
        </ConnectedAddressWrapper>
      )}
      {signer && !showRegistrationButton && (
        <FloatingButton onClick={() => setShowRegistrationButton(true)}>
          {userBeacon ? 'Update Beacon Location' : 'Register Beacon'}
        </FloatingButton>
      )}
      {showRegistrationButton && (
        <FloatingButton
          onClick={() => {
            // Clear any registration state when canceling
            setShowRegistrationButton(false);
            setPolygon(undefined);
            setOuterPolygon(undefined);
            setRegisteringCell(undefined);
          }}
          style={{ backgroundColor: COLORS.grey }}
        >
          Cancel
        </FloatingButton>
      )}
      {!provider && (
        <ModalWrapper>
          <ModalContent>
            <Title>Proximum Syene Testnet</Title>
            <h3>Connect your wallet to register a beacon or measure distances.</h3>
            <Button onClick={connectWallet}>Connect Wallet</Button>
          </ModalContent>
        </ModalWrapper>
      )}
      {showNetworkModal && (
        <ModalWrapper>
          <ModalContent>
            <Title>Wrong Network Detected</Title>
            <h3>Please switch to Ethereum Mainnet.</h3>
            <Button onClick={switchToMainnet}>Switch to Mainnet</Button>
            <Button onClick={() => setShowNetworkModal(false)}>Cancel</Button>
          </ModalContent>
        </ModalWrapper>
      )}
      {registeringCell && (
        <ModalWrapper onClick={() => setRegisteringCell(undefined)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <Title>{userBeacon ? 'Update Beacon Location' : 'Register Beacon'}</Title>
            <h3>Sign a message to register at H3 location: {registeringCell}.</h3>
            <Button onClick={registerBeacon}>Sign Message</Button>
            <Button
              onClick={() => setRegisteringCell(undefined)}
              style={{ backgroundColor: COLORS.grey }}
            >
              Cancel
            </Button>
          </ModalContent>
        </ModalWrapper>
      )}
    </>
  );
};